
import { mapState, mapActions } from 'vuex';
import ReviewFormFieldType from '@/enums/ReviewFormFieldType';
import MediaField from './MediaField';
import OptionField from './OptionField';
import FieldReward from './FieldReward';
import RewardDialog from './RewardDialog';
import ReviewMessageInput from './ReviewMessageInput';
import AiInfo from './AiInfo';
import AiClothingDetectionInfoDialog from './AiClothingDetectionInfoDialog';

export default {
  i18nComponentKey: 'ReviewsNew.components.ReviewForm',
  name: 'ReviewForm',
  components: {
    MediaField,
    OptionField,
    FieldReward,
    ReviewMessageInput,
    AiInfo
  },
  props: {
    formFields: { type: Array, required: true },
    fieldRewards: { type: Object, required: true },
    isReviewContentRestricted: { type: Boolean, required: true },
    isRewardableReview: { type: Boolean, required: true },
    validMessageLength: { type: Number, required: true }
  },
  computed: {
    ...mapState('form', ['formValues', 'fieldErrors']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ReviewFormFieldType: () => ReviewFormFieldType,
    mediaRewardDescription() {
      const rewards = this.fieldRewards.message;
      const photoReward = rewards.photo.maxValue;
      const videoReward = rewards.video.maxValue;
      const aiClothingReward = rewards.aiClothing.maxValue;

      const types = [];
      if (photoReward > 0) types.push('photo');
      if (videoReward > 0) types.push('video');
      if (aiClothingReward > 0) types.push('ai_clothing');

      if (types.length === 0) return null;

      return this.$t(`media_description.${types.join('_')}`, {
        reward: photoReward + videoReward + aiClothingReward
      });
    },
    textRewardDescription() {
      const segments = this.formFields.find(e => e.key === 'message')
        .review_reward_config.reward_segments;
      if (segments.length < 2) return '';

      const segmentsDescription = segments
        .map((segment, i) => {
          if (segment.base_value <= 0) return null;

          if (i === 0)
            return this.$t('reward_segment_detail.lower_bound', {
              min: segment.min_message_length,
              reward: segment.base_value
            });
          else if (i < segments.length - 1)
            return this.$t('reward_segment_detail.range', {
              min: segment.min_message_length,
              max: segments[i - 1].min_message_length,
              reward: segment.base_value
            });
          else
            return this.$t('reward_segment_detail.upper_bound', {
              max: segments[i - 1].min_message_length,
              reward: segment.base_value
            });
        })
        .filter(Boolean)
        .join(' / ');

      if (this.reviewSettings.auto_mileage_use_valid_review_message_length)
        return (
          segmentsDescription +
          this.$t('text_description_with_valid_message_length')
        );

      return segmentsDescription;
    },
    enableAiClothingDetection() {
      return this.formFields.find(e => e.key === 'message').field_config
        .enable_ai_clothing_detection;
    }
  },
  methods: {
    ...mapActions('form', ['setFieldValue', 'validateField']),
    ...mapActions('bottomSheet', ['openBottomSheet']),
    onClickTextRewardDetail() {
      const messageReward = this.fieldRewards.message;
      this.openBottomSheet({
        component: RewardDialog,
        props: {
          title: this.$t('text_detail_title'),
          rows: [
            {
              name: this.$t('text_review'),
              reason: this.$t('text_review_reason', {
                current: messageReward.currentMessageLength,
                max: messageReward.maxRewardMessageLength
              }),
              value: messageReward.text.currentValue,
              max: messageReward.text.maxValue
            },
            messageReward.aiKeyword.maxValue > 0
              ? {
                  name: this.$t('ai_keyword'),
                  reason: this.$t('ai_keyword_reason', {
                    current: messageReward.aiKeyword.currentCount,
                    max: messageReward.aiKeyword.maxCount
                  }),
                  value: messageReward.aiKeyword.currentValue,
                  max: messageReward.aiKeyword.maxValue
                }
              : null
          ].filter(Boolean),
          tableCaption: this.textRewardDescription
            ? this.$t('text_reward_description', [this.textRewardDescription])
            : null
        }
      });
    },
    onClickMediaRewardDetail() {
      const messageReward = this.fieldRewards.message;
      this.openBottomSheet({
        component: RewardDialog,
        props: {
          title: this.$t('media_detail_title'),
          rows: [
            {
              name: this.$t('photo_reward'),
              reason: this.$t('text_review_reason', {
                current: messageReward.currentMessageLength,
                max: messageReward.maxRewardMessageLength
              }),
              value: messageReward.photo.currentValue,
              max: messageReward.photo.maxValue
            },
            {
              name: this.$t('video_reward'),
              reason: this.$t('text_review_reason', {
                current: messageReward.currentMessageLength,
                max: messageReward.maxRewardMessageLength
              }),
              value: messageReward.video.currentValue,
              max: messageReward.video.maxValue
            },
            messageReward.aiClothing.maxValue > 0
              ? {
                  name: this.$t('ai_clothing_detected'),
                  value: messageReward.aiClothing.currentValue,
                  max: messageReward.aiClothing.maxValue
                }
              : null
          ].filter(Boolean)
        }
      });
    },
    onClickAiClothingDetectionInfoButton() {
      this.openBottomSheet({
        component: AiClothingDetectionInfoDialog
      });
    }
  }
};
